import React, { useState, useMemo, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, IconButton, CircularProgress, SvgIcon, InputAdornment
} from '@material-ui/core';
import { ReactComponent as searchIcon } from './../../assets/icons/searchIconNew.svg';
import { useTableWithSearchStyles } from './TableView.styles';

export interface TableColumn<T = any> {
  field: string;
  label: string;
  renderCell?: (row: T) => React.ReactNode;
  width?: number | string;
}

export interface TableViewProps<T = any> {
  columns: TableColumn<T>[] | string;
  rows: T[];
  searchConfig?: {
    mode: 'local' | 'api';
    apiFn?: (query: string) => Promise<T[]>;
    placeholder?: string;
    searchField?: string;
    searchFields?: string[];
    noDataMessage?: string;
    debounceMs?: number;
    onSearchChange?: (value: string) => void;
  };
}

// --- Helpers ---
const getCellStyle = (col: TableColumn) => ({
  width: typeof col.width === 'string' && col.width.includes('%') ? col.width : col.width || 'auto',
  maxWidth: typeof col.width === 'string' && col.width.includes('%') ? undefined : col.width || 400,
  whiteSpace: 'normal' as React.CSSProperties['whiteSpace'],
  wordBreak: 'break-word' as React.CSSProperties['wordBreak'],
});

// --- Hooks ---
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  React.useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

// --- Search Bar ---
interface SearchBarProps {
  value: string;
  onChange: (v: string) => void;
  onSearch: () => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
}
const SearchBar: React.FC<SearchBarProps> = ({
  value, onChange, onSearch, placeholder, loading, disabled
}) => {
  const classes = useTableWithSearchStyles();
  return (
    <div className={classes.searchBar}>
      <TextField
        variant="outlined"
        size="small"
        placeholder={placeholder || 'Search...'}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onSearch(); }}
        className={classes.searchInput}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                onClick={onSearch}
                disabled={loading}
                edge="start"
                aria-label="search"
              >
                {loading ? <CircularProgress size={12} /> : <SvgIcon component={searchIcon} />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />
    </div>
  );
};

function TableView<T = any>({ columns, rows, searchConfig }: TableViewProps<T>) {
  const classes = useTableWithSearchStyles();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiRows, setApiRows] = useState<T[]>([]);
  const [touched, setTouched] = useState(false);

  const debounceMs = searchConfig?.debounceMs ?? 0;
  const debouncedSearch = useDebouncedValue(search, debounceMs);
  const effectiveSearch = debounceMs > 0 ? debouncedSearch : search;

  // Normalize columns to array
  const normalizedColumns: TableColumn<T>[] = useMemo(() => {
    if (typeof columns === 'string') {
      return [{ field: columns, label: columns.charAt(0).toUpperCase() + columns.slice(1) }];
    }
    return columns;
  }, [columns]);

  const handleSearch = useCallback(async () => {
    setTouched(true);
    if (searchConfig?.mode === 'api' && searchConfig.apiFn) {
      setLoading(true);
      try {
        const result = await searchConfig.apiFn(search);
        setApiRows(result);
      } finally {
        setLoading(false);
      }
    }
  }, [search, searchConfig]);

  React.useEffect(() => {
    if (searchConfig?.mode === 'local' && debounceMs > 0) setTouched(true);
  }, [debouncedSearch, searchConfig?.mode, debounceMs]);

  React.useEffect(() => {
    if (searchConfig?.onSearchChange) searchConfig.onSearchChange(search);
  }, [search, searchConfig]);

  const filteredRows = useMemo(() => {
    if (searchConfig?.mode === 'api') return touched ? apiRows : rows;
    if (!effectiveSearch) return rows;
    const fields = searchConfig?.searchFields || [searchConfig?.searchField || normalizedColumns[0].field];
    return rows.filter(row =>
      fields.some(field =>
        String((row as any)[field] || '')
          .toLowerCase()
          .includes(effectiveSearch.toLowerCase())
      )
    );
  }, [effectiveSearch, apiRows, rows, searchConfig, normalizedColumns, touched]);

  return (
    <Paper className={classes.root} elevation={0}>
      {searchConfig && (
        <SearchBar
          value={search}
          onChange={setSearch}
          onSearch={handleSearch}
          placeholder={searchConfig.placeholder}
          loading={loading}
          disabled={loading && searchConfig.mode === 'api'}
        />
      )}
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table} stickyHeader>
          <TableHead>
            <TableRow>
              {normalizedColumns.map(col => (
                <TableCell
                  key={col.field}
                  className={classes.tableHeadCell}
                  style={getCellStyle(col)}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row, idx) => (
              <TableRow key={idx} style={{ width: 'auto' }}>
                {normalizedColumns.map(col => (
                  <TableCell
                    key={col.field}
                    className={classes.tableCell}
                    style={getCellStyle(col)}
                  >
                    {col.renderCell ? col.renderCell(row) : (row as any)[col.field]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={normalizedColumns.length} align="center" className={classes.tableCell}>
                  {searchConfig?.noDataMessage || 'No data found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default TableView;